import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { isPlatformAdminEmail } from '../../utils/platformAdmin'

/**
 * ProtectedRoute - Guards routes that require authentication
 *
 * Features:
 * - Redirects unauthenticated users to /login
 * - Saves attempted location for post-login redirect
 * - Shows loading state while checking auth
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The protected content
 */
function ProtectedRoute({ children }) {
  if (import.meta.env.DEV) return children

  const { isAuthenticated, emailVerified, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (!emailVerified) {
    return <Navigate to="/verify-email" replace />
  }

  return children
}

/**
 * AdminRoute - Guards routes that require platform admin access.
 * Redirects non-admins to /dashboard without showing any content.
 */
function AdminRoute({ children }) {
  if (import.meta.env.DEV) return children

  const { isAuthenticated, emailVerified, loading, user } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (!emailVerified) {
    return <Navigate to="/verify-email" replace />
  }

  if (!isPlatformAdminEmail(user?.email)) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

export default ProtectedRoute
export { AdminRoute }
